import { DatePipe } from "@angular/common";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { NavigationEnd, Router } from "@angular/router";
import {
  Observable,
  Subject,
  Subscription,
  concat,
  of,
  throwError,
} from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
} from "rxjs/operators";
import {
  MarketType,
  OperationType,
  Portfolio,
  SettlementType,
  TicketType,
} from "../../model/domain/Entity";
import { Fund } from "../../model/domain/Fund";
import { FundAccount } from "../../model/domain/FundAccount";
import { BankService } from "../../services/bank.service";
import { DialogService } from "../../services/dialog.service";
import { StockService } from "../../services/stock.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AllStockModel } from "./../../model/domain/AllStock";

export interface StockDialogData {
  fundSourceId: string;
  ticketType: TicketType;
}

export class ReportType {
  name: string;
}

@Component({
  selector: "app-dados-fundo",
  templateUrl: "./dados-fundo.component.html",
  styleUrls: ["./dados-fundo.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class DadosFundoComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    public validator: ValidatorService,
    private router: Router,
    private ticketService: TicketService,
    private bankService: BankService,
    private dialogService: DialogService,
    private datePipe: DatePipe,
    private stockService: StockService,
    @Inject(MAT_DIALOG_DATA) public data: StockDialogData
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isValidating) {
          this.resetForm();
        }
      }
    });
  }

  displayedColumns = ["bank", "branch"];

  @ViewChild("thisForm", { static: true }) fundForm: NgForm;
  @Input() portfolio: Portfolio = new Portfolio();

  @Input() ticketType: TicketType = null;

  @Input() marketType: MarketType = null;
  @Input() settlementType: SettlementType = null;
  @Input() operationType: OperationType = null;

  @Input() canValidate = false;
  @Input() canEdit = false;
  @Input() isCetipStructured;

  @Input() showHeader = true;
  @Input() documentIsDisabled = false;
  @Input() nameIsDisabled = false;
  @Input() fundAccountIsDisabled = false;

  @Input() isWallet: Boolean;
  @Input() isWalletUpload: Boolean;

  @Input() isTransferTicket = false;
  @Output() emitterSelectedFund: EventEmitter<any> = new EventEmitter();
  @Output() emitterSelectedFundAccount: EventEmitter<any> = new EventEmitter();
  @Output() emitterStockSelectedItem: EventEmitter<any> = new EventEmitter();
  @Output() emitterSelectedFundStock: EventEmitter<any> = new EventEmitter();

  stockData: AllStockModel = new AllStockModel();

  isValidating = false;
  formEnabled = true;

  funds: Observable<Fund[]>;
  fundSearchResult: Fund[] = null;
  fundSearchTerm = new Subject<string>();

  fundsByName: Observable<Fund[]>;
  fundsByDocument: Observable<Fund[]>;
  fundSearchNameTerm = new Subject<string>();
  fundSearchDocumentTerm = new Subject<string>();

  selectedFund: Fund = new Fund();
  fundAccountList: FundAccount[] = null;
  selectedFundAccount: FundAccount = null;
  reportTypesFunds: ReportType[] = [];
  selectedReportTypesFunds: ReportType[] = [];
  selectedReportTypesLiabilities: ReportType[] = [];
  file: File;

  validators = {
    document: (value) => this.validator.validateDocument(value),
    name: (value) => this.coalesce(value).length > 0,
    bank: (value) => this.coalesce(value).length > 0,
    branch: (value) => this.coalesce(value).length > 0,
    account: (value) => this.coalesce(value).length > 0,
    clearingAccount: (value) =>
      this.isCetip() ? this.coalesce(value).length > 0 : true,
  };

  navigationSubscription: Subscription;

  reportTypesLiabilities: ReportType[] = [
    { name: "Posição de Cotista" },
    { name: "IRRF" },
  ];

  reportCotistas: ReportType[] = [
    { name: "Joao da Silva" },
    { name: "Bernardo Pinto" },
    { name: "Eugenio Prestes" },
    { name: "Todos cotistas ativos" },
    { name: "Todos cotistas mesmo encerrados" },
  ];

  openFile(event: Event) {}

  submit() {}

  ngOnInit() {
    if (this.isWalletUpload) {
      this.reportTypesFunds = [
        { name: "Balancete Contábil" },
        { name: "Razão" },
        { name: "DRE" },
      ];
    } else {
      this.reportTypesFunds = [
        { name: "Carteira diária" },
        { name: "Extrato de Conta" },
        { name: "Cota diária (XML 4.0)" },
        { name: "Demonstrativo de Caixa" },
        { name: "Rentabilidade sintética" },
        { name: "Extrato Mensal" },
        { name: "Cotas investimento" },
        { name: "Movimento diário" },
        { name: "Cota diária (XML 5.0)" },
      ];
    }
    this.setEnable(this.canEdit);
    this.searchFunds();
  }

  ngOnChanges() {
    this.setEnable(this.canEdit);
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.selectedFund = new Fund();
    this.selectedFundAccount = null;
    this.fundAccountList = null;
    this.fundForm.resetForm();

    this.setEnable(true);
  }

  setEnable(enabled: boolean = false) {
    this.formEnabled = enabled;
    setTimeout(() => {
      if (enabled) {
        Object.values(this.fundForm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit) {
        Object.values(this.fundForm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  public validatePortfolioFields() {
    this.isValidating = true;
    Object.values(this.fundForm.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    if (this.fundForm.invalid) {
      return throwError("Verifique os campos destacados.");
    }

    this.isValidating = false;

    return null;
  }

  searchFunds() {
    this.fundsByName = concat(
      of([]),
      this.fundSearchNameTerm.pipe(
        filter((term) => term?.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((name) =>
          this.ticketService.Funds(name, null).pipe(catchError(() => of([])))
        )
      )
    );

    this.fundsByDocument = concat(
      of([]),
      this.fundSearchDocumentTerm.pipe(
        filter((term) => term.length >= 3),
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((document) =>
          this.ticketService
            .Funds(null, document)
            .pipe(catchError(() => of([])))
        )
      )
    );
  }

  fillComponentWhenFundSelected() {
    this.portfolio.name = "";
    this.portfolio.document = "";
    this.portfolio.clearingAccount = "";
    this.portfolio.fundId = null;
    this.portfolio.sourceId = "";

    this.portfolio.bank = "";
    this.portfolio.branch = "";
    this.portfolio.account = "";
    this.portfolio.fundAccountId = null;

    this.selectedFundAccount = null;

    if (this.selectedFund) {
      this.selectedFund.document = this.validator.formatDocument(
        this.selectedFund.document
      );

      this.portfolio.name = this.selectedFund.name;
      this.portfolio.document = this.selectedFund.document;
      this.portfolio.sourceId = this.selectedFund.sourceId;

      this.portfolio.fundId = this.selectedFund.id;

      if (this.isCetip() && this.selectedFund.cetipAccount !== "0") {
        this.portfolio.clearingAccount = this.selectedFund.cetipAccount;
      }

      if (this.isSelic() && this.selectedFund.selicAccount !== "0") {
        this.portfolio.clearingAccount = this.selectedFund.selicAccount;
      }

      if (this.isB3() && this.selectedFund.b3Account !== "0") {
        this.portfolio.clearingAccount = this.selectedFund.b3Account;
      }

      this.ticketService
        .getFundAccount(this.selectedFund.id)
        .subscribe((res) => {
          this.fundAccountList = res;

          if (this.fundAccountList.length === 1 && !this.isTransferTicket) {
            this.portfolio.bank = this.fundAccountList[0].bank;
            this.portfolio.branch = this.fundAccountList[0].branch;
            this.portfolio.account = this.fundAccountList[0].account;
            this.bankService.sendBank(this.portfolio.bank);

            this.portfolio.fundAccountId = this.fundAccountList[0].id;

            this.selectedFundAccount = this.fundAccountList[0];
            this.emitterSelectedFundAccount.emit(this.selectedFundAccount);
          }
          if (this.isTransferTicket) {
            let emitData: any = {
              selectedFund: this.selectedFund,
              fundAccountList: this.fundAccountList,
              selectedFundAccount: this.selectedFundAccount,
            };
            this.emitterSelectedFund.emit(emitData);

            if (this.selectedFundAccount) {
              this.emitterSelectedFundAccount.emit(this.selectedFundAccount);
            }
          }
        });
    }
  }

  fillComponentWhenFundAccountSelected() {
    this.portfolio.bank = this.selectedFundAccount.bank;
    this.portfolio.branch = this.selectedFundAccount.branch;
    this.portfolio.account = this.selectedFundAccount.account;

    this.portfolio.fundAccountId = this.selectedFundAccount.id;

    if (this.isTransferTicket) {
      this.emitterSelectedFundAccount.emit(this.selectedFundAccount);
    }
    this.bankService.sendBank(this.selectedFundAccount.bank);
  }

  public fillComponentFieldsLoad<T>(model: any) {
    let data: any;
    data = Object.assign({}, model);
    try {
      if (this.ticketType !== TicketType.CurrencyTerm) {
        this.portfolio.document = this.selectedFund.document =
          data.portfolioDocument;
        this.portfolio.name = this.selectedFund.name = data.portfolioName;
        this.portfolio.fundId = this.selectedFund.id = data.portfolioFundId;

        this.selectedFundAccount = new FundAccount();
        this.portfolio.bank = this.selectedFundAccount.bank =
          data.portfolioBank;
        this.portfolio.branch = this.selectedFundAccount.branch =
          data.portfolioBranch;
        this.portfolio.account = this.selectedFundAccount.account =
          data.portfolioAccount;
        this.portfolio.fundAccountId = this.selectedFundAccount.id =
          data.portfolioFundAccountId;
        this.bankService.sendBank(this.selectedFundAccount.bank);
        this.settlementType = data.settlementTypeId;

        if (this.isCetip()) {
          if (this.isCetipStructured) {
            this.ticketService
              .Funds(this.selectedFund.name)
              .subscribe((res) => {
                res.forEach((x) => {
                  if (this.selectedFund.name === x.name)
                    this.portfolio.clearingAccount = x.cetipAccount;
                });
              });
          } else {
            this.portfolio.clearingAccount = this.selectedFund.cetipAccount =
              data.portfolioCetipAccount;
          }
        }

        if (this.isSelic()) {
          this.portfolio.clearingAccount = this.selectedFund.selicAccount =
            data.portfolioSelicAccount;
        }

        if (this.isB3()) {
          this.portfolio.clearingAccount = this.selectedFund.b3Account =
            data.portfolioB3Account;
        }

        if ([TicketType.Margin, TicketType.Loan].includes(this.ticketType)) {
          this.portfolio.clearingAccount = data.portfolioClearingAccount;
        }
      } else {
        // If TicketType === CurrencyTerm. CurrencyTerm possui um objeto para portfolio.
        this.portfolio.document = this.selectedFund.document =
          data.portfolio.document;
        this.portfolio.name = this.selectedFund.name = data.portfolio.name;
        this.portfolio.fundId = this.selectedFund.id = data.portfolio.fundId;

        this.portfolio.clearingAccount = data.portfolio.clearingAccount;

        this.selectedFundAccount = new FundAccount();
        this.portfolio.bank = this.selectedFundAccount.bank =
          data.portfolio.bank;
        this.portfolio.branch = this.selectedFundAccount.branch =
          data.portfolio.branch;
        this.portfolio.account = this.selectedFundAccount.account =
          data.portfolio.account;
        this.portfolio.fundAccountId = this.selectedFundAccount.id =
          data.portfolio.fundAccountId;
      }
    } catch (error) {
      return throwError(error);
    }
  }

  public fillPortfolioData(data: any, model: Portfolio): any {
    if (this.ticketType !== TicketType.CurrencyTerm) {
      data.portfolioName = model.name;
      data.portfolioBank = model.bank;
      data.portfolioBranch = model.branch;
      data.portfolioAccount = model.account;

      data.portfolioFundId = model.fundId;
      data.portfolioFundAccountId = model.fundAccountId;

      if (model.document != null) {
        data.portfolioDocument = this.validator.sanitize(model.document);
      }

      if (model.clearingAccount != null) {
        if (this.isCetip()) {
          data.portfolioCetipAccount = model.clearingAccount;
        }

        if (this.isSelic()) {
          data.portfolioSelicAccount = model.clearingAccount;
        }

        if (this.isB3()) {
          data.portfolioB3Account = model.clearingAccount;
        }

        if ([TicketType.Margin, TicketType.Loan].includes(this.ticketType)) {
          data.portfolioClearingAccount = model.clearingAccount;
        }
      }
    } else {
      data.portfolio.name = model.name;
      data.portfolio.bank = model.bank;
      data.portfolio.branch = model.branch;
      data.portfolio.account = model.account;
      data.portfolio.clearingAccount = model.clearingAccount;

      data.portfolio.fundId = model.fundId;
      data.portfolio.fundAccountId = model.fundAccountId;

      if (model.document != null) {
        data.portfolio.document = this.validator.sanitize(model.document);
      }
    }
    return data;
  }

  public fillReceiverComponentFieldsLoad<T>(model: any) {
    let data: any;
    data = Object.assign({}, model);
    this.portfolio.document = this.selectedFund.document =
      data.receiverDocument;
    this.portfolio.name = this.selectedFund.name = data.receiverName;
    this.portfolio.fundId = this.selectedFund.id = data.receiverFundId;

    this.selectedFundAccount = new FundAccount();
    this.portfolio.bank = this.selectedFundAccount.bank = data.receiverBank;
    this.portfolio.branch = this.selectedFundAccount.branch =
      data.receiverBranch;
    this.portfolio.account = this.selectedFundAccount.account =
      data.receiverAccount;
    this.portfolio.fundAccountId = this.selectedFundAccount.id =
      data.receiverFundAccountId;
  }

  public fillReceiverData(data: any, model: Portfolio): any {
    if (this.ticketType !== TicketType.CurrencyTerm) {
      data.receiverName = model.name;
      data.receiverBank = model.bank;
      data.receiverBranch = model.branch;
      data.receiverAccount = model.account;

      data.receiverFundId = model.fundId;
      data.receiverFundAccountId = model.fundAccountId;

      if (model.document != null) {
        data.receiverDocument = this.validator.sanitize(model.document);
      }
    }
    return data;
  }

  isB3() {
    if (
      [TicketType.VariableIncome, TicketType.Futures, TicketType.Loan].includes(
        this.ticketType
      )
    ) {
      return true;
    } else if (
      this.ticketType === TicketType.Margin &&
      this.marketType === MarketType.VariableIncome
    ) {
      return true;
    }
  }

  isCetip() {
    if (
      (this.ticketType === TicketType.CETIP &&
        this.settlementType !== SettlementType.TED &&
        !this.isCetipStructured) ||
      this.ticketType === TicketType.CurrencyTerm ||
      this.ticketType === TicketType.SwapCetip ||
      this.ticketType === TicketType.Exchange ||
      (this.settlementType === SettlementType.NoFinancial &&
        !this.isCetipStructured) ||
      this.settlementType === SettlementType.Sovereign
    ) {
      return true;
    } else if (
      (this.ticketType === TicketType.Margin &&
        this.marketType === MarketType.FixedIncomePrivateAsset) ||
      (this.ticketType === TicketType.Quota &&
        this.settlementType === SettlementType.CETIP)
    ) {
      return true;
    }
  }

  isSelic() {
    if (
      this.ticketType === TicketType.SELIC ||
      this.ticketType === TicketType.Contracted
    ) {
      return true;
    } else if (
      this.ticketType === TicketType.Margin &&
      this.marketType === MarketType.FixedIncomeGovernmentBond
    ) {
      return true;
    }
  }

  isTEDOrMoney() {
    if (
      (this.ticketType === TicketType.Quota &&
        this.settlementType === SettlementType.TED) ||
      (this.ticketType === TicketType.Margin &&
        this.marketType === MarketType.Cash) ||
      (this.ticketType === TicketType.CETIP &&
        this.settlementType === SettlementType.TED)
    ) {
      return true;
    }
  }

  isNoFinancial() {
    if (
      this.ticketType === TicketType.Quota &&
      this.settlementType === SettlementType.NoFinancial
    ) {
      return true;
    }
  }

  showBankSelect() {
    if (this.fundAccountList !== null && this.fundAccountList.length >= 2) {
      return true;
    }
    return false;
  }

  isSovereign() {
    return this.settlementType === SettlementType.Sovereign;
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.portfolio[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.portfolio[k])) {
        validationErrors.push(k);
      }
    });

    if (this.fundForm.invalid) {
      validationErrors.push("form is invalid");
    }

    return validationErrors;
  }

  coalesce(value: any) {
    return value || "";
  }

  ticketTypes() {
    return TicketType;
  }

  openStockFundsDialog() {
    setTimeout(() => {
      this.dialogService
        .stockFundsDialog(this.portfolio.sourceId, this.ticketType)
        .subscribe((res) => {
          this.emitterStockSelectedItem.emit(res);
        });
    }, 0);
  }

  getAllStock(fundSourceId: string) {
    setTimeout(() => {
      this.stockService.getAllStock(fundSourceId).subscribe((res) => {
        this.stockData = res;
        this.emitterSelectedFundStock.emit(this.stockData);
      });
    }, 0);
  }

  viewStockButton() {
    return (
      (this.ticketType == TicketType.Quota ||
        this.ticketType == TicketType.CETIP ||
        this.ticketType == TicketType.SELIC ||
        this.ticketType == TicketType.Contracted) &&
      (this.operationType == OperationType.FullRedemption ||
        this.operationType == OperationType.Redemption)
    );
  }
}
